import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { setError } from './errorSlice';
import { addNotification } from './notificationSlice';
import { startLoading, stopLoading } from './loadingSlice';
import { UserService } from '../../api/generated/services/UserService';
import { ApiError } from '../../api/generated/core/ApiError';

interface FreshdeskState {
  apiKey: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: FreshdeskState = {
  apiKey: null,
  loading: false,
  error: null,
};

export const fetchFreshdeskApiKey = createAsyncThunk(
  'freshdesk/fetchApiKey',
  async (_, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await UserService.getAppUserFreshdeskApiKey();
      // Handle different response formats
      const apiKey = response.data?.apiKey || (response as any).apiKey || null;
      return { apiKey };
    } catch (err) {
      // 404 means user has no license - this is not an error, just no API key
      if (err instanceof ApiError && err.status === 404) {
        return { apiKey: null };
      }
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch Freshdesk API key';
      dispatch(setError(errorMessage));
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      }));
      throw err;
    } finally {
      dispatch(stopLoading());
    }
  }
);

const freshdeskSlice = createSlice({
  name: 'freshdesk',
  initialState,
  reducers: {
    clearApiKey: (state) => {
      state.apiKey = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFreshdeskApiKey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFreshdeskApiKey.fulfilled, (state, action) => {
        state.loading = false;
        state.apiKey = action.payload.apiKey;
      })
      .addCase(fetchFreshdeskApiKey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch Freshdesk API key';
      });
  },
});

export const { clearApiKey } = freshdeskSlice.actions;
export default freshdeskSlice.reducer;
